import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    executeMatching,
    fetchLatestMatchingResult,
    fetchQuota,
} from '../../services/matchingApi.js';
import { fetchAllJobPoolItems } from '../../services/jobPoolApi.js';
import { hasExtractedResume } from '../../services/resumeApi.js';
import useReorderSave from './useReorderSave';
import useScoreAdjustments from './useScoreAdjustments';
import useCandidateVisibility from './useCandidateVisibility';
import useReasonOverrides from './useReasonOverrides';
import { anySavingRef } from './savingRefs';
import { MATCHING_MAX_SELECTED_JOBS } from './matchingLimits';
import { SCOPE_OWN, initialScope, isInOwnScope, itemsInScope } from './jobPoolScope';

const DEFAULT_FILTERS = {
    salaryMin: '',
    salaryMax: '',
};

// 求人プールの取得の失敗の文言 (SCR-M-001 §6.4.3。jobPoolApi.js の失敗の結果と同じ文言)
const JOB_POOL_FETCH_ERROR_MESSAGE = '求人プールの取得に失敗しました。';

/**
 * 表示順を解決し、求人プールと突合して orderedItems を構築する。
 *
 * 並べ替え確定順 (reorderedIds、matching_reorder_history 由来、Issue #548) があれば
 * それを優先し、無ければ AI 推薦順 (recommendations の rank 昇順) にフォールバックする
 * (PDF出力 services/matching_pdf.py::resolve_effective_order と同じ優先順位)。
 * rank は表示順に合わせて 1 始まりで振り直す (moveItem の並べ替え結果と同じ扱い)。
 *
 * @param {object[]} recommendations
 * @param {string[]|null|undefined} reorderedIds
 * @param {Map<string, object>} jobPoolItemsById
 */
function buildOrderedItemsFrom(recommendations, reorderedIds, jobPoolItemsById) {
    const recommendationsById = new Map(recommendations.map((rec) => [rec.job_pool_item_id, rec]));
    const orderedIds =
        reorderedIds && reorderedIds.length > 0
            ? reorderedIds
            : [...recommendations].sort((a, b) => a.rank - b.rank).map((rec) => rec.job_pool_item_id);

    return orderedIds
        .map((id, index) => {
            const rec = recommendationsById.get(id);
            if (!rec) return null; // 整合性が取れない場合は該当項目を除外
            return { ...rec, rank: index + 1, jobPoolItem: jobPoolItemsById.get(id) || null };
        })
        .filter(Boolean);
}

/**
 * 画面を表示したときの選択 (IN-M-007) を決める (画面レイアウト_SCR-M-001.md §6.1、Issue #779)。
 *
 * 表示する推薦結果があれば、その結果の実行に使った選択 (filters.job_pool_item_ids) を復元する。
 * 一覧に無い ID は、呼び出し側で一覧との共通部分を取るため件数・送信に含まれない。
 * 表示する結果が無ければ、「この求職者のために上げた求人だけ」(IN-M-013 の own) に出る求人を全件選ぶ
 * (§4.6.6 #5、Issue #782。一覧はテナントの求人の全件のため、ほかの方の求人は選ばない)。
 *
 * @param {object[]} jobPoolItems テナントの求人の全件
 * @param {object|null} restoredResult
 * @param {(item: object) => boolean} inOwnScope own に出るか
 * @returns {Set<string>}
 */
function initialSelection(jobPoolItems, restoredResult, inOwnScope) {
    const restoredIds = restoredResult?.filters?.job_pool_item_ids;
    if (Array.isArray(restoredIds)) return new Set(restoredIds);
    // 結果に job_pool_item_ids が無い (#956 のデプロイ前に作られた結果) ときも、
    // 当時の実行は求職者の全件が対象だったため own の全件を選ぶ (PM 確認 2026-09-17、Issue #779)
    return new Set(jobPoolItems.filter(inOwnScope).map((item) => item.id));
}

/**
 * 選択が、表示中の推薦結果の選択と異なるか (DP-M-013。画面レイアウト_SCR-M-001.md §4.2、Issue #707)。
 *
 * 一覧にある求人ごとに「結果の選択に入っているか」と「今の選択に入っているか」を比べる。
 * 一覧に無い ID は比べない (§6.1)。結果に job_pool_item_ids が無いときは一覧の全件を結果の選択とする。
 * この「全件」は比べる時点の一覧のため、表示中に取り込んだ求人でも相違にならない (§4.2 DP-M-013 ② の例外)。
 *
 * @param {object[]} jobPoolItems
 * @param {Set<string>} selectedIdSet
 * @param {object} displayedResult
 * @param {(item: object) => boolean} inOwnScope
 * @returns {boolean}
 */
function selectionDiffersFrom(jobPoolItems, selectedIdSet, displayedResult, inOwnScope) {
    const resultSelection = initialSelection(jobPoolItems, displayedResult, inOwnScope);
    return jobPoolItems.some((item) => resultSelection.has(item.id) !== selectedIdSet.has(item.id));
}

/**
 * AI マッチング画面 (SCR-M-001) の状態・API 連携ロジック。
 *
 * phase: idle(未実行) / loading(実行中) / results(結果あり) / empty(0件) / error(エラー)
 * DP-M-006 準拠。求人プール一覧の全件取得・再取得（`refreshJobPool`）、マッチングの対象にする求人の
 * 選択 (IN-M-007・008、DP-M-011)、選択と表示中の結果の相違 (DP-M-013)、マッチング実行を担う。
 * 求人ファイルの取込 UI 自体（ドロップゾーン、T-001-U1）は `JobUploadZone` が別コンポーネントとして担当し、
 * 呼び出し元（`RecommendationList`）が取込完了時に `selectImportedJobPoolItem` を呼び出して一覧を同期する。
 *
 * @param {string} opportunityId
 * @param {{profile?: string}|null} opportunity 求職者情報の事前バリデーション用(Issue #591)
 * @param {number|null} meetingsTotal 面談件数の事前バリデーション用(Issue #591、nullは未取得)
 * @param {object[]|null} resumeAttachments 履歴書一覧の事前バリデーション用(Issue #1057、nullは未取得)
 * @param {string|null|undefined} currentUserId ログイン中の利用者の ID (「だけ」の範囲の判定。Issue #782)
 */
function useAiMatching(opportunityId, opportunity, meetingsTotal, resumeAttachments, currentUserId) {
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    // テナントの求人の全件 (§6.4.3「第 2 段階の取り方」、Issue #782)。表示するのは jobPoolScope で絞った分
    const [jobPoolItems, setJobPoolItems] = useState([]);
    // 表示する求人 (IN-M-013。§4.6.6)。画面を表示したときに選択から決める
    const [jobPoolScope, setJobPoolScope] = useState(SCOPE_OWN);
    // 「全部」から「だけ」に戻して選択を外したときの文言 (§4.6.4。DP-M-013 と同じ role="status" の領域に出す)
    const [jobPoolScopeStatus, setJobPoolScopeStatus] = useState(null);
    const [jobPoolLoading, setJobPoolLoading] = useState(true);
    const [jobPoolError, setJobPoolError] = useState(null);
    // 選んだ求人の ID。一覧に無い ID を含みうるため、件数・送信は一覧との共通部分で数える (§6.1)
    const [selectedIdSet, setSelectedIdSet] = useState(() => new Set());

    // {used_points, max_points, remaining_points, required_points}。required_points は操作ごとの
    // 必要なポイント (PBI-002 §11.6、Issue #882)。取得できていない間は null (§6.4.4「取得できないとき」①)
    const [quota, setQuota] = useState(null);
    const [quotaLoading, setQuotaLoading] = useState(true);

    const [phase, setPhase] = useState('idle');
    // 'quota'（AI ポイントの上限。No.3）| 'network'（通信の失敗。No.2。再試行あり）
    // | 'server'（5xx。No.5。再試行あり＋ポイント消費の注意）| 'client'（No.1・No.4。再試行なし）
    // | 'validation'（API 呼出前の事前チェック）| null。SCR-M-001 §6.3（Issue #868）
    const [errorType, setErrorType] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);
    const [matchingResult, setMatchingResult] = useState(null);
    const [orderedItems, setOrderedItems] = useState([]);
    const [savedOrderIds, setSavedOrderIds] = useState(null);
    // 並べ替えの保存の文言を消す関数 (useReorderSave が返す。実行の処理が先に作られるため ref で持つ)
    const clearReorderMessagesRef = useRef(() => {});

    // 求人プールを全件取得して state に反映し、取得した配列を返す (取得中表示は呼び出し側が持つ)。
    // 例外は投げない (画面を表示したときの取得の失敗。SCR-M-001 §6.3、Issue #1388)
    const fetchJobPool = useCallback(async () => {
        let result;
        try {
            // 案件で絞らずテナントの求人を全件取る (§6.4.3「第 2 段階の取り方」、Issue #782)
            result = await fetchAllJobPoolItems(null);
        } catch (err) {
            // 通信の失敗は fetchJobPoolItems が失敗の結果で返す。ここに来るのは、成功の応答で
            // 本文を解析できない等の想定外の例外 (取得の失敗として扱う。§6.4.3)
            console.error('useAiMatching: 求人プールの取得で例外が発生しました', err);
            result = { ok: false, errorMessage: JOB_POOL_FETCH_ERROR_MESSAGE };
        }
        if (result.ok) {
            setJobPoolItems(result.items);
            setJobPoolError(null);
            return result.items;
        }
        setJobPoolItems([]);
        setJobPoolError(result.errorMessage);
        return [];
    }, []);

    // own に出るか (§4.6.6 #2)。判定の本体は jobPoolScope.js の 1 か所
    const inOwnScope = useCallback(
        (item) => isInOwnScope(item, opportunityId, currentUserId),
        [opportunityId, currentUserId]
    );

    const loadJobPoolItems = useCallback(async () => {
        setJobPoolLoading(true);
        const items = await fetchJobPool();
        setJobPoolLoading(false);
        return items;
    }, [fetchJobPool]);

    // 取り直しに失敗したときは前に取れた値を残す (消すと判定が「押せる」に倒れ、
    // 取り直しのたびに押せる・押せないが揺れるため。§6.4.4「取得できないとき」③)
    // 例外は投げない。runMatching は待たずに呼ぶため、投げると未処理の拒否になり
    // 「取得中」のまま残る (SCR-M-001 §6.3「画面を表示したときの取得の失敗」、Issue #1388)
    const loadQuota = useCallback(async () => {
        setQuotaLoading(true);
        try {
            const result = await fetchQuota();
            if (result.ok) {
                setQuota(result.data);
            }
        } catch (err) {
            // 通信の失敗は fetchQuota が失敗の結果で返す。ここに来るのは本文を解析できない等
            console.error('useAiMatching: AI ポイント残量の取得で例外が発生しました', err);
        } finally {
            setQuotaLoading(false);
        }
    }, []);

    // マウント時に直近のマッチング結果があれば、実行からの経過時間を問わず自動復元する
    // (FR-MATCH-005、Issue #541。経過時間を問わないのは #710。SCR-M-001 §6.1「画面復帰」)。
    // 求人プールと突合するため jobPoolItems の取得結果を直接引数で受け取る
    // (jobPoolItemsById は useMemo 経由の state 由来でこの時点ではまだ反映されておらず、
    // 参照すると空の Map を見てしまうスケールレースになるため使わない)。
    // 復元した結果を返す (選択の復元に使う。結果が無ければ null)。
    // 例外は投げず「結果なし」として null を返す (SCR-M-001 §6.3「画面を表示したときの取得の失敗」、Issue #1388)
    const restoreCachedResult = useCallback(
        async (jobPoolItemsForRestore) => {
            try {
                const result = await fetchLatestMatchingResult(opportunityId);
                if (!result.ok) return null; // 結果なし (未実行。200 の null・404 等): 通常通り idle のまま

                const recommendations = result.data.recommendations || [];
                // 状態は並びを組み立ててから入れる (組み立てで例外が起きたときに、結果だけが入り
                // phase が idle のままという食い違った状態を残さないため。Issue #1388)
                const built =
                    recommendations.length === 0
                        ? []
                        : buildOrderedItemsFrom(
                            recommendations,
                            result.data.reordered_ids,
                            new Map(jobPoolItemsForRestore.map((item) => [item.id, item]))
                        );

                setMatchingResult(result.data);
                setOrderedItems(built);
                setSavedOrderIds(built.length === 0 ? null : built.map((item) => item.job_pool_item_id));
                setPhase(recommendations.length === 0 ? 'empty' : 'results');
                return result.data;
            } catch (err) {
                // 通信の失敗・本文の解析の失敗は fetchLatestMatchingResult が失敗の結果で返す。
                // ここに来るのは応答を受け取った後の処理の例外等
                console.error('useAiMatching: 推薦結果の画面復帰で例外が発生しました', err);
                return null;
            }
        },
        [opportunityId]
    );

    useEffect(() => {
        // 後から始まった init があれば、前の init は選択・開き方を書かない (Issue #782)。
        // 利用者の ID が後から取れると init は 2 回走り、先に始めた方 (ID 無し) が後に終わると
        // ID 無しで決めた選択が残るため。ID を待たずに走らせるのは、ID が取れないときに
        // 一覧が「取得中」のまま残らないようにするため
        let superseded = false;
        async function init() {
            // 選択を決めるまでは一覧を「取得中」のままにする (決まる前の操作が復元で上書きされないように)。
            // 呼ぶ 3 つの関数は例外を投げないため、ここを try で囲まなくても「取得中」は必ず解ける
            // (Issue #1388。関数の側で捕まえるのは、取込完了時・実行の後など init の外からも呼ばれるため)
            setJobPoolLoading(true);
            const [jobPoolItemsResult] = await Promise.all([fetchJobPool(), loadQuota()]);
            const restored = await restoreCachedResult(jobPoolItemsResult);
            if (superseded) return; // 「取得中」を解くのは後から始まった init
            const selection = initialSelection(jobPoolItemsResult, restored, inOwnScope);
            setSelectedIdSet(selection);
            // 選択に own に出ない求人があれば「全部」で開く (§4.6.6 #6)
            setJobPoolScope(initialScope(jobPoolItemsResult, selection, opportunityId, currentUserId));
            setJobPoolLoading(false);
        }
        init();
        return () => {
            superseded = true;
        };
    }, [fetchJobPool, loadQuota, restoreCachedResult, inOwnScope, opportunityId, currentUserId]);

    const jobPoolItemsById = useMemo(() => {
        const map = new Map();
        jobPoolItems.forEach((item) => map.set(item.id, item));
        return map;
    }, [jobPoolItems]);

    // 一覧に表示する求人 (DP-M-009。IN-M-013 で絞る。§4.6.6)
    const visibleJobPoolItems = useMemo(
        () => itemsInScope(jobPoolItems, jobPoolScope, opportunityId, currentUserId),
        [jobPoolItems, jobPoolScope, opportunityId, currentUserId]
    );

    // 一覧の並び (取込の新しい順) で、選んだ求人の ID を並べる (§6.1: 一覧に無い ID は含めない)。
    // 表示している求人の中から取る (隠れている求人は選択を外しているが、見えない求人を送らないことをここでも守る。
    // §4.6.6 #7)
    const selectedJobPoolItemIds = useMemo(
        () => visibleJobPoolItems.filter((item) => selectedIdSet.has(item.id)).map((item) => item.id),
        [visibleJobPoolItems, selectedIdSet]
    );
    const selectedCount = selectedJobPoolItemIds.length;
    // IN-M-008 の 3 状態 (§4.6.1 論点 3)。表示している求人で決める (§4.1 IN-M-008)
    let selectionState = 'some';
    if (selectedCount === 0) selectionState = 'none';
    else if (selectedCount === visibleJobPoolItems.length) selectionState = 'all';

    // DP-M-013 を出すか。出すのは推薦結果 (0 件の案内を含む) を表示している間だけで、
    // 実行中・実行エラーの間と、画面を表示して選択を決めるまで (一覧の取得中) は出さない (§4.2 DP-M-013 ①)
    const isShowingResult = (phase === 'results' || phase === 'empty') && matchingResult !== null;
    const selectionDiffersFromResult = useMemo(
        () =>
            isShowingResult &&
            !jobPoolLoading &&
            selectionDiffersFrom(jobPoolItems, selectedIdSet, matchingResult, inOwnScope),
        [isShowingResult, jobPoolLoading, jobPoolItems, selectedIdSet, matchingResult, inOwnScope]
    );

    // IN-M-013 の切り替え (§4.6.6 #7)。「全部」から「だけ」に戻すと、隠れる求人の選択を外し、外した件数を知らせる。
    // 「だけ」から「全部」にしたときは選択を変えない (加わる求人は選ばれていない状態で出る)
    const changeJobPoolScope = useCallback(
        (nextScope) => {
            setJobPoolScope(nextScope);
            if (nextScope !== SCOPE_OWN) {
                setJobPoolScopeStatus(null);
                return;
            }
            const hiddenIds = jobPoolItems
                .filter((item) => selectedIdSet.has(item.id) && !inOwnScope(item))
                .map((item) => item.id);
            if (hiddenIds.length === 0) {
                setJobPoolScopeStatus(null);
                return;
            }
            setSelectedIdSet((prev) => {
                const next = new Set(prev);
                hiddenIds.forEach((id) => next.delete(id));
                return next;
            });
            setJobPoolScopeStatus(`表示していない ${hiddenIds.length} 件の求人の選択を外しました。`);
        },
        [jobPoolItems, selectedIdSet, inOwnScope]
    );

    const toggleJobPoolItem = useCallback((jobPoolItemId) => {
        setSelectedIdSet((prev) => {
            const next = new Set(prev);
            if (next.has(jobPoolItemId)) next.delete(jobPoolItemId);
            else next.add(jobPoolItemId);
            return next;
        });
    }, []);

    // 求人プールから削除できた求人を、一覧 (DP-M-009)・選択 (IN-M-007)・画面の並びから取り除く
    // (SCR-M-001 §6.2「求人プールからの削除（BTN-M-014）」手順 3、Issue #883)。推薦結果の要素は残し
    // (§4.4「並び」)、その求人のカードは求人プールに無い求人の枠に変わる。一覧は取り直さない
    const removeJobPoolItem = useCallback((jobPoolItemId) => {
        setJobPoolItems((prev) => prev.filter((item) => item.id !== jobPoolItemId));
        setSelectedIdSet((prev) => {
            if (!prev.has(jobPoolItemId)) return prev;
            const next = new Set(prev);
            next.delete(jobPoolItemId);
            return next;
        });
        setOrderedItems((prev) =>
            prev.map((item) => (item.job_pool_item_id === jobPoolItemId ? { ...item, jobPoolItem: null } : item))
        );
    }, []);

    // すべて選択のときに押すと全解除、それ以外のときに押すと全選択 (§4.6.1 論点 3)
    const toggleSelectAll = useCallback(() => {
        // 表示している求人に掛ける (§4.6.5 #3。隠れている求人は選ばれていないため、全解除も表示分で足りる)
        setSelectedIdSet(
            selectionState === 'all' ? new Set() : new Set(visibleJobPoolItems.map((item) => item.id))
        );
    }, [selectionState, visibleJobPoolItems]);

    // 取込が完了した求人を選択に加え、一覧を取り直す (§6.2 手順 4・5)。
    // 新規の求人は作成日時が最新のため一覧の先頭に入る。既存求人の更新は位置を変えずチェックを入れる。
    // 取り直しの間は「取得中」にしない (取込は 1 件ずつ完了するため、置き換えると一覧が何度も消える)
    const selectImportedJobPoolItem = useCallback(
        async (jobPoolItem) => {
            if (jobPoolItem?.id) {
                setSelectedIdSet((prev) => new Set(prev).add(jobPoolItem.id));
            }
            return fetchJobPool();
        },
        [fetchJobPool]
    );

    const buildOrderedItems = useCallback(
        (recommendations, reorderedIds) => buildOrderedItemsFrom(recommendations, reorderedIds, jobPoolItemsById),
        [jobPoolItemsById]
    );

    const runMatching = useCallback(async () => {
        // 求職者情報(profile)・面談履歴・読み取れた履歴書のいずれも無い場合、AIマッチングの
        // 元になる情報が無く実行できない(BE側 query_text が空文字列になりVertex AI呼出が
        // 失敗する、Issue #591)。API呼出前にバリデーションし、原因不明のエラーに
        // せず明示的に通知する(叶様SR方針)。meetingsTotal が未取得(null)の間は
        // 判定を保留し、誤って実行をブロックしないようにする。
        //
        // 履歴書の条件は Issue #1043 で BE の実行条件が広がったことへの追随
        // (画面レイアウト_SCR-M-001.md §6.3)。条件を古いままにすると、profile が空・
        // 面談ゼロ・しかし読み取れた履歴書がある案件で、BE は実行できるのに FE が手前で止める。
        const hasProfile = Boolean(opportunity?.profile && opportunity.profile.trim());
        const hasMeetingHistory = meetingsTotal === null || meetingsTotal === undefined ? true : meetingsTotal > 0;
        // 一覧が未取得(null)の間は meetingsTotal と同じく判定を保留する。取得済みで
        // text_extracted が無い場合(古い BE)は hasExtractedResume が null を返すため、
        // 履歴書を条件に入れず改める前と同じ判定に戻す(§6.3 の注記、Issue #1057 AC ③)。
        const hasReadableResume = Array.isArray(resumeAttachments)
            ? hasExtractedResume(resumeAttachments) === true
            : true;
        if (!hasProfile && !hasMeetingHistory && !hasReadableResume) {
            setPhase('error');
            setErrorType('validation');
            setErrorMessage(
                '求職者情報が未入力で、面談記録も、読み取れた履歴書もないため、AIマッチングを実行できません。'
                + '求職者情報を入力するか、面談を実施するか、履歴書を添付してから再度お試しください。'
            );
            return;
        }
        // 選択 0 件・上限超えでは実行しない (BTN-M-002 ⑤、§5.2 No.4・No.8。ボタンも押せない。
        // エラーの「再試行」はフォームのボタンを通らないため、ここでも止める)
        if (selectedCount === 0 || selectedCount > MATCHING_MAX_SELECTED_JOBS) return;

        setPhase('loading');
        setErrorType(null);
        setErrorMessage(null);
        // 並べ替えの保存の失敗の文言と「保存できた直後」の文を消す (改める前と同じく、実行を始めた時点で。
        // 前回と同じ結果がそのまま使われる再実行でも消す)。並べ替えのフックはこの後で作るため ref で呼ぶ
        clearReorderMessagesRef.current();

        const result = await executeMatching(opportunityId, filters, selectedJobPoolItemIds);

        // 実行によりAIポイントが消費されうる(キャッシュヒット時は変動なし)ため、成否に関わらず残量を再取得する
        loadQuota();

        if (!result.ok) {
            setPhase('error');
            // 応答で再試行の可否と文言を分ける (画面レイアウト_SCR-M-001.md §6.3
            // 「AI マッチングの実行のエラー」の表。Issue #868)。**自動リトライはしない**——
            // 実行は Phase 2 (AI 呼出。成功するとポイントを消費) → Phase 3 (DB 書き込み) の順で、
            // Phase 3 で失敗すると消費済みのまま 5xx になるため、自動でやり直すと
            // 利用者が押していないのにポイントが減る (同表の注記 ①)
            if (result.status === 400 && /AIクレジット|上限/.test(result.errorMessage || '')) {
                setErrorType('quota'); // No.3
            } else if (result.retryable && result.status === 0) {
                // No.2 通信の失敗。届いていない可能性と、届いて Phase 2 まで進んだ
                // 可能性の両方がある（取込の表 No.8 と同じ状況）。No.5 と同じく
                // ポイント消費の注意を出す——違いは応答が返ったかどうかだけ
                setErrorType('network');
            } else if (result.retryable) {
                // No.5 5xx。消費の有無を知れないため、注意を添えて手動の再試行を出す
                setErrorType('server');
            } else {
                setErrorType('client'); // No.1・No.4: やり直しても同じ結果になるため出さない
            }
            setErrorMessage(result.errorMessage);
            return;
        }

        setMatchingResult(result.data);
        const recommendations = result.data.recommendations || [];

        if (recommendations.length === 0) {
            setPhase('empty');
            setOrderedItems([]);
            setSavedOrderIds(null);
            return;
        }

        const built = buildOrderedItems(recommendations, result.data.reordered_ids);
        setOrderedItems(built);
        setSavedOrderIds(built.map((item) => item.job_pool_item_id));
        setPhase('results');
    }, [
        opportunityId,
        filters,
        selectedCount,
        selectedJobPoolItemIds,
        buildOrderedItems,
        loadQuota,
        opportunity,
        meetingsTotal,
        resumeAttachments,
    ]);

    // 求職者に提案するか (推薦 PDF に載せるか。IN-M-011。Issue #992) と、マッチ度の調整
    // (表示・非表示の切り替え IN-M-009 と、数字の書き換え IN-M-010。Issue #1435)。
    // 状態と保存はそれぞれのフックに分けた (本ファイルを大きくしないため)。どちらかの保存中は他を呼ばない
    // (同じ推薦結果の行をロックして書くため。SCR-M-001 §6.2「同時に保存するもの」)。
    // 互いの保存中は ref で渡し合い、押した時点の値で判定する (useCandidateVisibility.js の説明)。
    // マッチ度の保存中の ref はここで作り、マッチ度のフックが保存の開始・終了で書く。
    // おすすめポイントの文の保存 (IN-M-012。Issue #777) も同じ並びに加え、3 つのどれかが保存中の間は
    // 他を呼ばない。各フックには「ほかの 2 つのどちらかが保存中か」をまとめた ref を渡す (savingRefs.js)
    // 並べ替えの保存もマッチ度を書くようになったため、同じ並びに加える (Issue #2173。
    // §6.2「同時に保存するもの」)。各フックに渡す ref には並べ替えの保存中も含める。
    // 並べ替えは動かしてから少し待って送るため、待っている間 (reorderWaitingRef) も保存中として含める (Issue #2462)
    const scoreSavingRef = useRef(false);
    const reasonsSavingRef = useRef(false);
    const reorderSavingRef = useRef(false);
    const reorderWaitingRef = useRef(false);
    const scoreOrReasonsSavingRef = useMemo(
        () => anySavingRef(scoreSavingRef, reasonsSavingRef, reorderSavingRef, reorderWaitingRef), []
    );
    const { candidateVisibilitySavingRef, ...candidateVisibility } = useCandidateVisibility(
        matchingResult, setMatchingResult, setOrderedItems, scoreOrReasonsSavingRef
    );
    const candidateOrReasonsSavingRef = useMemo(
        () => anySavingRef(candidateVisibilitySavingRef, reasonsSavingRef, reorderSavingRef, reorderWaitingRef),
        [candidateVisibilitySavingRef]
    );
    const scoreAdjustments = useScoreAdjustments(
        matchingResult, setMatchingResult, setOrderedItems, candidateOrReasonsSavingRef, scoreSavingRef
    );
    const scoreOrCandidateSavingRef = useMemo(
        () => anySavingRef(scoreSavingRef, candidateVisibilitySavingRef, reorderSavingRef, reorderWaitingRef),
        [candidateVisibilitySavingRef]
    );
    const reasonOverrides = useReasonOverrides(
        matchingResult, setMatchingResult, setOrderedItems, scoreOrCandidateSavingRef, reasonsSavingRef
    );
    // 並べ替えの自動の保存。マッチ度・求職者に提案するか・おすすめポイントの文のどれかの保存中と、マッチ度の
    // 数字の編集中は送らず、終わってから待ち始める (Issue #2173・#2462)
    const scoreCandidateOrReasonsSavingRef = useMemo(
        () => anySavingRef(scoreSavingRef, candidateVisibilitySavingRef, reasonsSavingRef),
        [candidateVisibilitySavingRef]
    );
    const scoreEditing = scoreAdjustments.editingScoreItemId !== null;
    const otherSettingSaving = Boolean(
        scoreAdjustments.scoreOverrideSaving || scoreAdjustments.scoreVisibilitySaving
        || candidateVisibility.candidateVisibilitySaving || reasonOverrides.reasonsOverrideSaving
    );
    const { notifyReorderMoved, clearReorderMessages, ...reorderSave } = useReorderSave(
        matchingResult,
        setMatchingResult,
        orderedItems,
        setOrderedItems,
        savedOrderIds,
        setSavedOrderIds,
        scoreCandidateOrReasonsSavingRef,
        reorderSavingRef,
        reorderWaitingRef,
        scoreEditing,
        otherSettingSaving
    );
    clearReorderMessagesRef.current = clearReorderMessages;

    // 手動並べ替え (BTN-M-005、職業安定法対応の Agent-in-the-loop UI)。
    // ブラウザ内の表示順を即時更新し、少し待ってから PUT /api/matching/reorder で自動で保存する
    // (matching_reorder_history に記録。Issue #443・#2462)。
    // マッチ度の数字の編集中は動かさない (動かすとすぐ保存して数字を付け直すため、編集中の数字とぶつかる。
    // 画面もどのカードも動かせなくする。Issue #2462)
    const moveItem = useCallback((jobPoolItemId, direction) => {
        if (scoreEditing) return;
        setOrderedItems((prev) => {
            const index = prev.findIndex((item) => item.job_pool_item_id === jobPoolItemId);
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (index < 0 || targetIndex < 0 || targetIndex >= prev.length) return prev;

            const next = [...prev];
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next.map((item, i) => ({ ...item, rank: i + 1 }));
        });
        notifyReorderMoved();
    }, [scoreEditing, notifyReorderMoved]);

    // 手動並べ替え D&D 版 (Issue #596、設計書 画面レイアウト_SCR-M-001.md §機能一覧 FR-MATCH-003)。
    // moveItem (↑↓ボタン、1つ隣との入替) とは異なり、任意の位置への移動を1操作で行う。
    // 保存は moveItem と共通 (ドラッグを離したときに待ち始める。ドラッグの途中では保存しない。Issue #2462)。
    // 同じ位置に落としたとき (並びが変わらないとき) も「保存できた直後」の文は消す。並びが保存済みと
    // 同じなら送らない (useReorderSave が判定する)
    const moveItemToIndex = useCallback((jobPoolItemId, destinationIndex) => {
        if (scoreEditing) return;
        setOrderedItems((prev) => {
            const sourceIndex = prev.findIndex((item) => item.job_pool_item_id === jobPoolItemId);
            if (sourceIndex < 0) return prev;

            const clampedDestination = Math.max(0, Math.min(destinationIndex, prev.length - 1));
            if (sourceIndex === clampedDestination) return prev;

            const next = [...prev];
            const [moved] = next.splice(sourceIndex, 1);
            next.splice(clampedDestination, 0, moved);
            return next.map((item, i) => ({ ...item, rank: i + 1 }));
        });
        notifyReorderMoved();
    }, [scoreEditing, notifyReorderMoved]);

    // マッチ度の数字・おすすめポイントの文の編集中に実行したら、編集をやめる (入力した値は捨てる。
    // SCR-M-001 §6.2「マッチ度の数字の書き換え」手順 10・「おすすめポイントの文の編集」手順 10)。
    // 前回と同じ結果がそのまま使われる再実行 (結果の ID が同じ) で、実行の後にカードが編集中で出ないようにする。
    // 保存中 (応答待ち) はどちらの cancel も何もしない (同「既知の制約」)
    const { cancelScoreEdit } = scoreAdjustments;
    const { cancelReasonsEdit } = reasonOverrides;
    const runMatchingEndingScoreEdit = useCallback(() => {
        cancelScoreEdit();
        cancelReasonsEdit();
        return runMatching();
    }, [cancelScoreEdit, cancelReasonsEdit, runMatching]);

    const updateFilters = useCallback((patch) => {
        setFilters((prev) => ({ ...prev, ...patch }));
    }, []);

    const resetFilters = useCallback(() => setFilters(DEFAULT_FILTERS), []);

    return {
        filters,
        updateFilters,
        resetFilters,
        jobPoolItems,
        jobPoolLoading,
        jobPoolError,
        // テナントの求人の件数 (§5.2 No.3 の「求人プール」。Issue #782)
        jobPoolCount: jobPoolItems.length,
        // 表示する求人 (IN-M-013・DP-M-008・009・021。§4.6.6、Issue #782)
        visibleJobPoolItems,
        jobPoolScope,
        changeJobPoolScope,
        jobPoolScopeStatus,
        refreshJobPool: loadJobPoolItems,
        selectedIdSet,
        selectedCount,
        selectionState,
        selectionDiffersFromResult,
        toggleJobPoolItem,
        toggleSelectAll,
        selectImportedJobPoolItem,
        // 求人プールから削除できた求人を画面から取り除く (§6.2「求人プールからの削除（BTN-M-014）」、Issue #883)
        removeJobPoolItem,
        quota,
        quotaLoading,
        // 取込確定・PDF 出力の失敗の後に呼び出し側が残量を取り直す (§6.4.4「取り直す時機」③・④、Issue #882)
        refreshQuota: loadQuota,
        phase,
        errorType,
        errorMessage,
        matchingResult,
        orderedItems,
        runMatching: runMatchingEndingScoreEdit,
        moveItem,
        moveItemToIndex,
        // 並べ替えの自動の保存 (Issue #443・#2173・#2462)。返す項目は useReorderSave.js を参照
        ...reorderSave,
        // マッチ度の調整 (IN-M-009・IN-M-010、Issue #1435)。返す項目は useScoreAdjustments.js を参照
        ...scoreAdjustments,
        // 求職者に提案するか (IN-M-011、Issue #992)。返す項目は useCandidateVisibility.js を参照
        ...candidateVisibility,
        // おすすめポイントの文の編集 (IN-M-012、Issue #777)。返す項目は useReasonOverrides.js を参照
        ...reasonOverrides,
    };
}

export default useAiMatching;
